import { type ReactNode } from 'react';
import { ExternalLink } from 'lucide-react';
import { Avatar } from '@/components/ui/avatar';
import type { Creator } from '@/types';
import { creatorPhotoUrl } from '../utils/creator-formatters';
import { StateBadge } from './creator-state-badges';

// `alert_pending_at` es la única señal de atención: la rellena tanto el
// admin (vía /api/leads/alert) como la IA (vía hook lead-attention-flag
// cuando ai_analysis_status pasa a 'rejected_by_ai'). El origen se
// distingue con `alerted_by`: con valor = admin, vacío = IA.
type Reason = { title: string; label: string };
const ADMIN_REASON: Reason = {
  title: 'Alerta pendiente: revisa este creador',
  label: 'Alerta pendiente',
};
const AI_REASON: Reason = {
  title: 'IA alertó sobre este creador: revísalo y apela o elimina',
  label: 'Alertado por IA',
};

function attentionReason(creator: Creator): Reason | null {
  // Si el collector ya descartó el lead, su decisión final prevalece sobre
  // la alerta de IA: mostramos el badge "Descartado" (vía StateBadge) y
  // ocultamos el ⚠️ para no duplicar señales.
  if (creator.user_deleted_at) return null;
  if (!creator.alert_pending_at) return null;
  return creator.alerted_by ? ADMIN_REASON : AI_REASON;
}

function AttentionMark({ creator }: { creator: Creator }): ReactNode {
  const reason = attentionReason(creator);
  if (!reason) return null;
  return (
    <span
      className="text-xs leading-none"
      title={reason.title}
      aria-label={reason.label}
    >
      ⚠️
    </span>
  );
}

function UserText({ creator }: { creator: Creator }): ReactNode {
  const dim = !!creator.is_void || !!creator.deleted_at;
  const nameCls = `truncate text-[13px] text-text-primary ${dim ? 'line-through' : ''}`;
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-xs">
        <AttentionMark creator={creator} />
        <p className={nameCls}>@{creator.ig_username}</p>
        <StateBadge creator={creator} />
      </div>
      <p className="truncate text-[11px] text-text-tertiary">
        {creator.display_name}
      </p>
    </div>
  );
}

export function UserCell({ creator }: { creator: Creator }): ReactNode {
  return (
    <div className="flex items-center gap-sm">
      <Avatar
        src={creatorPhotoUrl(creator)}
        name={creator.ig_username}
        size="sm"
      />
      <UserText creator={creator} />
    </div>
  );
}

export function PostLink({ url }: { url: string }): ReactNode {
  if (!url) return <span className="text-text-tertiary/30">—</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="text-text-tertiary hover:text-brand-red"
    >
      <ExternalLink size={13} />
    </a>
  );
}
